import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { del, get, patch, post } from "src/utils/networkUtils";

const AREA_LIMIT = 20;
const COUNTRY_LIMIT = 100;

function normalizeAreaRows(payload) {
  const items = payload?.data?.items ?? payload?.items ?? [];
  return Array.isArray(items) ? items : [];
}

function normalizeCountryRows(payload) {
  const items = payload?.data?.items ?? payload?.items ?? [];
  return Array.isArray(items) ? items : [];
}

export default function AreasTabPage() {
  const [areas, setAreas] = useState([]);
  const [countries, setCountries] = useState([]);
  const [areaPage, setAreaPage] = useState(1);
  const [areaPagination, setAreaPagination] = useState({
    page: 1,
    limit: AREA_LIMIT,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [dialogMode, setDialogMode] = useState(null); // "create" | "edit" | "delete" | null
  const [selectedArea, setSelectedArea] = useState(null);
  const [form, setForm] = useState({ name: "", type: "continent" });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeDialog = () => {
    setDialogMode(null);
    setSelectedArea(null);
    setForm({ name: "", type: "continent" });
  };

  const fetchAllCountries = useCallback(async () => {
    const firstPage = await get(`/admin/country?page=1&limit=${COUNTRY_LIMIT}`);
    const firstItems = normalizeCountryRows(firstPage);
    const pagination =
      firstPage?.data?.pagination ?? firstPage?.pagination ?? {};
    const totalPages = Number(pagination.totalPages ?? 1);

    if (totalPages <= 1) return firstItems;

    const pageRequests = [];
    for (let page = 2; page <= totalPages; page += 1) {
      pageRequests.push(
        get(`/admin/country?page=${page}&limit=${COUNTRY_LIMIT}`)
      );
    }

    const pageResponses = await Promise.all(pageRequests);
    const otherItems = pageResponses.flatMap((response) =>
      normalizeCountryRows(response)
    );
    return [...firstItems, ...otherItems];
  }, []);

  const fetchAreasPage = useCallback(async () => {
    setLoading(true);
    try {
      const areasResponse = await get(
        `/admin/area?page=${areaPage}&limit=${AREA_LIMIT}`
      );
      const pagination =
        areasResponse?.data?.pagination ?? areasResponse?.pagination;
      setAreas(normalizeAreaRows(areasResponse));
      if (pagination) {
        setAreaPagination({
          page: Number(pagination.page ?? areaPage),
          limit: Number(pagination.limit ?? AREA_LIMIT),
          total: Number(pagination.total ?? 0),
          totalPages: Number(pagination.totalPages ?? 1),
        });
      }
    } catch (error) {
      console.error("Failed to fetch areas/countries:", error);
      showSnackbar("Failed to load areas data", "error");
    } finally {
      setLoading(false);
    }
  }, [areaPage]);

  useEffect(() => {
    fetchAreasPage();
  }, [fetchAreasPage]);

  useEffect(() => {
    fetchAllCountries()
      .then((allCountries) => setCountries(allCountries))
      .catch((error) => {
        console.error("Failed to fetch countries for area counts:", error);
        showSnackbar("Failed to load countries counts", "error");
      });
  }, [fetchAllCountries]);

  const countryCountByAreaId = useMemo(() => {
    const counts = new Map();
    countries.forEach((country) => {
      if (!country.area_id) return;
      counts.set(country.area_id, (counts.get(country.area_id) ?? 0) + 1);
    });
    return counts;
  }, [countries]);

  const openCreate = () => {
    setDialogMode("create");
    setSelectedArea(null);
    setForm({ name: "", type: "continent" });
  };

  const openEdit = (area) => {
    setDialogMode("edit");
    setSelectedArea(area);
    setForm({
      name: area.name ?? "",
      type: area.type ?? "continent",
    });
  };

  const openDelete = (area) => {
    const countriesCount = countryCountByAreaId.get(area.id) ?? 0;
    if (countriesCount > 0) {
      showSnackbar(
        `Cannot delete "${
          area.name
        }" because it has ${countriesCount} assigned countr${
          countriesCount === 1 ? "y" : "ies"
        }.`,
        "error"
      );
      return;
    }
    setDialogMode("delete");
    setSelectedArea(area);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      showSnackbar("Area name is required", "error");
      return;
    }
    setSubmitting(true);
    try {
      await post("/admin/area", {
        name: form.name.trim(),
        type: form.type,
      });
      showSnackbar("Area created successfully");
      closeDialog();
      await Promise.all([
        fetchAreasPage(),
        fetchAllCountries().then((allCountries) => setCountries(allCountries)),
      ]);
    } catch (error) {
      console.error("Create area failed:", error);
      showSnackbar("Failed to create area", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedArea?.id) return;
    if (!form.name.trim()) {
      showSnackbar("Area name is required", "error");
      return;
    }
    setSubmitting(true);
    try {
      await patch(`/admin/area/${selectedArea.id}`, {
        name: form.name.trim(),
        type: form.type,
      });
      showSnackbar("Area updated successfully");
      closeDialog();
      await Promise.all([
        fetchAreasPage(),
        fetchAllCountries().then((allCountries) => setCountries(allCountries)),
      ]);
    } catch (error) {
      console.error("Update area failed:", error);
      showSnackbar("Failed to update area", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedArea?.id) return;
    const countriesCount = countryCountByAreaId.get(selectedArea.id) ?? 0;
    if (countriesCount > 0) {
      showSnackbar(
        `Cannot delete "${
          selectedArea.name
        }" because it has ${countriesCount} assigned countr${
          countriesCount === 1 ? "y" : "ies"
        }.`,
        "error"
      );
      closeDialog();
      return;
    }
    setSubmitting(true);
    try {
      await del(`/admin/area/${selectedArea.id}`);
      showSnackbar("Area deleted successfully");
      closeDialog();
      await Promise.all([
        fetchAreasPage(),
        fetchAllCountries().then((allCountries) => setCountries(allCountries)),
      ]);
    } catch (error) {
      console.error("Delete area failed:", error);
      showSnackbar("Failed to delete area", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Add New Area
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Area Name</TableCell>
              <TableCell>Countries Count</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {areas.map((area) => (
              <TableRow key={area.id} hover>
                <TableCell>{area.name}</TableCell>
                <TableCell>
                  <Chip
                    label={countryCountByAreaId.get(area.id) ?? 0}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-label={`Edit ${area.name}`}
                    color="primary"
                    onClick={() => openEdit(area)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label={`Delete ${area.name}`}
                    color="error"
                    disabled={(countryCountByAreaId.get(area.id) ?? 0) > 0}
                    onClick={() => openDelete(area)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {!loading && areas.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  No areas found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {areaPagination.totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <Pagination
            page={areaPage}
            count={areaPagination.totalPages}
            color="primary"
            shape="rounded"
            onChange={(_, page) => setAreaPage(page)}
          />
        </Box>
      )}

      <Dialog
        open={dialogMode === "create"}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Area</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Area Name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Type"
              select
              value={form.type}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, type: e.target.value }))
              }
              fullWidth
            >
              <MenuItem value="continent">Continent</MenuItem>
              <MenuItem value="region">Region</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={submitting}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogMode === "edit"}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Area</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Area Name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Type"
              select
              value={form.type}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, type: e.target.value }))
              }
              fullWidth
            >
              <MenuItem value="continent">Continent</MenuItem>
              <MenuItem value="region">Region</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={handleEdit}
            variant="contained"
            disabled={submitting}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogMode === "delete"}
        onClose={closeDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Area</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete{" "}
            <strong>{selectedArea?.name ?? "this area"}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={submitting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
